import { ensure, Optional } from "sprint-common-web";

import { MovieVersionDocumentResponse } from "../../../../../../common/api/catalogue-api/movie/MovieDocumentResponse";
import { EpisodeVersionDocumentResponse } from "../../../../../../common/api/catalogue-api/tv-api/TvSeriesDocumentResponse";
import { useContentApi } from "../../../../../../common/data/data-hooks/content/useContentApi";
import { ContentVersion } from "../../../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../../../../common/types/ContentSubtype";
import { SubmissionsHistoryContent, mapToVersionText } from "../../../../lookup-history/useLookupHistory";
import { ContentAssociationFormState } from "../../../types/ContentAssociationFormState";

interface UseMapToSubmissionsHistoryContent {
    mapToSubmissionsHistoryContent: <T extends ContentAssociationFormState>(
        formState: T
    ) => Promise<Optional<SubmissionsHistoryContent>>;
}

export const useMapToSubmissionsHistoryContent = (): UseMapToSubmissionsHistoryContent => {
    const { getMovieById, getTvSeriesById, getAncillaryById } = useContentApi();

    const mapToSubmissionsHistoryContent = async <T extends ContentAssociationFormState>(
        formState: T
    ): Promise<Optional<SubmissionsHistoryContent>> => {
        const { contentId, contentType } = formState;

        if (!contentType || !contentId) {
            return undefined;
        }

        switch (contentType) {
            case ContentReferenceType.Movie: {
                const movie = await getMovieById(contentId);
                const versions = movie?.versions.filter((v) => formState.versionIds?.includes(v.id));

                return {
                    type: movie.subtype,
                    id: movie?.id ?? "",
                    title: movie?.title ?? "",
                    versions: versions?.map(mapToHistoryVersion) ?? []
                };
            }

            case ContentReferenceType.TvSeries: {
                const tvSeries = await getTvSeriesById(contentId);

                const season = tvSeries.seasons.find((s) => s.id === formState.seasonId);

                const episode = season?.episodes.find((s) => s.id === formState.episodeId);

                const episodeVersions = episode?.versions
                    ? getMovieOrEpisodeVersions(episode.versions, formState.versionIds)
                    : [];

                return {
                    type: ContentSubtype.TV,
                    id: tvSeries?.id ?? "",
                    episodeId: episode?.id ?? "",
                    seasonId: season?.id ?? "",
                    title: tvSeries?.title ?? "",
                    versions: episodeVersions?.map(mapToHistoryVersion) ?? []
                };
            }

            case ContentReferenceType.Ancillary: {
                const ancillary = await getAncillaryById(contentId);
                const version = ancillary?.version;
                const airline = ancillary.version.airlines.find((a) => a.id === formState.airlineId);

                const versions = version ? [mapToHistoryVersion(version)] : [];

                ensure(airline, "Airline not found for ancillary");

                return {
                    type: ancillary.subtype,
                    id: ancillary?.id ?? "",
                    airline,
                    title: ancillary?.title ?? "",
                    versions
                };
            }
        }
    };

    return { mapToSubmissionsHistoryContent };
};

const mapToHistoryVersion = (version: ContentVersion) => {
    return { id: version.id, name: mapToVersionText(version.distributor, version.type, version.brandings) };
};

const getMovieOrEpisodeVersions = (
    versions: (MovieVersionDocumentResponse | EpisodeVersionDocumentResponse)[],
    versionIds?: string[]
) => {
    return versions
        .filter((v) => versionIds?.includes(v.id))
        .map((v) => {
            return {
                id: v.id,
                distributor: v.distributor,
                buzzId: v.buzzVersion?.id,
                type: v.type,
                brandings: v.brandings
            };
        });
};
